import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import { ArrowRight, Users, Sparkles, CalendarRange, Wallet, Heart } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SignInButton, SignUpButton } from "@clerk/react";
import heroDesktop from "@/assets/generated_images/hero-desktop.webp";
import heroMobile from "@/assets/generated_images/hero-mobile.webp";
import { trackEvent } from "@/lib/analytics";

export function HeroSection() {
  const { t } = useTranslation();
  const handleCtaClick = (cta: string) => trackEvent("landing_cta_clicked", { cta });
  const pills = [
    { icon: CalendarRange, labelKey: "hero.highlightCalendar", testId: "text-hero-highlight-calendar" },
    { icon: Heart, labelKey: "hero.highlightMatch", testId: "text-hero-highlight-match" },
    { icon: Wallet, labelKey: "hero.highlightRoles", testId: "text-hero-highlight-roles" },
    { icon: Sparkles, labelKey: "hero.highlightAI", testId: "text-hero-highlight-ai" },
  ];

  const titleWords = t("hero.title").split(" ");
  const lastWord = titleWords.pop();
  const leadingWords = titleWords.join(" ");

  return (
    <section className="relative min-h-screen flex items-end lg:items-center overflow-hidden" data-testid="section-hero">
      {/* Background image — mobile-optimized portrait crop + desktop landscape */}
      <div
        className="absolute inset-0 animate-hero-drift lg:will-change-transform hidden md:block"
        style={{
          backgroundImage: heroDesktop ? `url(${heroDesktop})` : undefined,
          backgroundColor: heroDesktop ? undefined : "hsl(var(--brand-navy))",
          backgroundSize: "cover",
          backgroundPosition: "30% center",
        }}
      />
      <div
        className="absolute inset-0 animate-hero-drift md:hidden"
        style={{
          backgroundImage: heroMobile ? `url(${heroMobile})` : undefined,
          backgroundColor: heroMobile ? undefined : "hsl(var(--brand-navy))",
          backgroundSize: "cover",
          backgroundPosition: "center 30%",
        }}
      />

      {/* Cinematic overlay — light wash so background image stays vivid */}
      <div className="absolute inset-0 bg-brand-navy/35" />
      {/* Bottom vignette for depth — subtle */}
      <div className="absolute inset-0 from-brand-navy/20 via-transparent to-transparent bg-gradient-to-t" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pb-24 pt-32 lg:py-0">
        <div className="max-w-2xl mx-auto text-center">

          {/* Hero title — editorial split */}
          <Heading
            level={1}
            serif
            className="text-[2.75rem] sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-black text-white mb-7 animate-reveal-up"
            style={{ animationDelay: "0.25s", textShadow: "0 2px 12px rgba(0,0,0,0.5)" }} /* lint-colors-ignore */
          >
            {leadingWords}{" "}
            <span className="relative inline-block">
              <span className="text-brand-gold">{lastWord}</span>
              <span
                className="absolute -bottom-1.5 left-0 right-0 h-[3px] bg-brand-gold/60 rounded-full"
              />
            </span>
          </Heading>

          <Text
            className="sm:text-lg lg:text-xl text-white/85 mb-10 max-w-lg mx-auto animate-reveal-up"
            style={{ animationDelay: "0.4s", textShadow: "0 1px 8px rgba(0,0,0,0.4)" }} /* lint-colors-ignore */
          >
            {t("hero.subtitle")}
          </Text>

          {/* Feature pills — staggered reveal */}
          <div className="flex flex-wrap justify-center gap-2.5 mb-10">
            {pills.map((pill, i) => {
              const Icon = pill.icon;
              return (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] backdrop-blur-sm px-4 py-2 text-sm text-white/90 animate-reveal-up"
                  style={{ animationDelay: `${0.5 + i * 0.08}s` }}
                  data-testid={pill.testId}
                >
                  <Icon className="h-3.5 w-3.5 text-primary-foreground/80" />
                  {t(pill.labelKey)}
                </span>
              );
            })}
          </div>

          {/* CTAs */}
          <div
            className="flex flex-col sm:flex-row gap-4 items-center justify-center mb-8 animate-reveal-up"
            style={{ animationDelay: "0.7s" }}
          >
            <SignUpButton mode="redirect">
              <Button
                size="lg"
                className="bg-brand-gold hover:bg-brand-gold/90 text-brand-navy font-bold text-base px-8 py-6 rounded-full shadow-lg shadow-brand-gold/20 animate-pulse-glow transition-all duration-300 hover:scale-[1.03] hover:shadow-xl hover:shadow-brand-gold/30"
                data-testid="button-get-started"
                onClick={() => handleCtaClick("get_started")}
              >
                {t("hero.getStarted")}
                <ArrowRight className="ml-1.5 h-5 w-5" />
              </Button>
            </SignUpButton>
            <SignInButton mode="redirect">
              <Button
                size="lg"
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 hover:border-white/30 rounded-full px-8 py-6 backdrop-blur-sm transition-all duration-300"
                data-testid="button-browse-travelers"
                onClick={() => handleCtaClick("browse_companions")}
              >
                {t("hero.browseCompanions")}
              </Button>
            </SignInButton>
          </div>

          <div
            className="flex items-center justify-center gap-2.5 text-primary-foreground/80 animate-reveal-up"
            style={{ animationDelay: "0.85s" }}
            data-testid="text-hero-join-adventurers"
          >
            <Users className="h-4 w-4" />
            <span className="text-sm tracking-wide">{t("hero.joinAdventurers")}</span>
          </div>
        </div>
      </div>

      {/* Scroll indicator — minimal line pulse */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20" data-testid="scroll-indicator">
        <div className="flex flex-col items-center gap-2 animate-scroll-indicator">
          <div className="w-5 h-9 border border-white/25 rounded-full flex items-start justify-center pt-2">
            <div className="w-0.5 h-2 bg-white/60 rounded-full" />
          </div>
        </div>
      </div>
    </section>
  );
}
